import { toast } from "@risalabs_frontend_org/oasis-ui-kit";
import { useCallback, useState } from "react";
import { fetchAuthGridData } from "../../../../../api/postCall/authGridApi";
import { ToastMessages } from "../../../../../components/toasts/toastMessages";
import { NarAgentGridData } from "../../../../../data-model/medicalPaOrdersModel";
import { MedicalPaOrdersAuthStatus } from "../../../../../enums/medicalPaOrdersAuthStatus";

const filterNoAuthRequiredRows = (
  rows: NarAgentGridData[] = [],
): NarAgentGridData[] =>
  rows.filter(
    (row) => row.auth_status === MedicalPaOrdersAuthStatus.NoAuthRequired,
  );

export interface FetchNarAgentGridParams {
  payerName: string;
  jCodes: string[];
  clientId: string;
}

export interface FetchNarAgentGridOptions {
  silent?: boolean;
}

/**
 * Manages auth grid search state and API calls for the NAR Agent Grid page.
 */
export const useNarAgentGridAuthGridSearch = () => {
  const [apiData, setApiData] = useState<NarAgentGridData[]>([]);
  const [isLoadingAuthGrid, setIsLoadingAuthGrid] = useState(false);

  const clearResults = useCallback(() => {
    setApiData([]);
  }, []);

  const fetchAuthGrid = useCallback(
    async (
      params: FetchNarAgentGridParams,
      options?: FetchNarAgentGridOptions,
    ) => {
      setIsLoadingAuthGrid(true);
      try {
        const jCodes = params.jCodes
          .map((code) => code.trim())
          .filter((code) => code.length > 0);

        const response = await fetchAuthGridData({
          payer_name: params.payerName,
          j_codes: jCodes.length > 0 ? jCodes : [],
          client_id: params.clientId,
        });

        if (response.success) {
          const filteredData = filterNoAuthRequiredRows(response.data);
          setApiData(filteredData);
          if (!options?.silent) {
            if (filteredData.length === 0) {
              toast.error(ToastMessages.AUTH_GRID_NO_DATA);
            } else {
              toast.success(ToastMessages.AUTH_GRID_SUCCESS);
            }
          }
        } else {
          console.error("Failed to fetch auth grid data:", response.message);
          if (!options?.silent) {
            toast.error(ToastMessages.AUTH_GRID_FAILURE);
          }
        }
      } catch (error) {
        console.error("Error fetching auth grid data:", error);
        if (!options?.silent) {
          toast.error(ToastMessages.AUTH_GRID_ERROR);
        }
      } finally {
        setIsLoadingAuthGrid(false);
      }
    },
    [],
  );

  return {
    apiData,
    isLoadingAuthGrid,
    fetchAuthGrid,
    clearResults,
  };
};

export default useNarAgentGridAuthGridSearch;
